/* eslint-disable */
import React, { useMemo, useState } from 'react';
import { Controller, UseFormReturn, useForm } from 'react-hook-form';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';
import { DateRange } from 'src/components/RangeDatePicker/types';
import RHFInput from 'src/components/elements/Input/RHFInput';
import { Button, Chip, Popover } from '@mui/material';
import { twMerge } from 'tailwind-merge';
import { BiChevronDown } from 'react-icons/bi';
import { RangeDatePicker } from 'src/components';
import { enUS } from 'date-fns/locale';
import RHFMultiAutoComplete from 'src/components/elements/MultiAutoComplete/RHFMultiAutoComplete';
import { useClients } from 'src/hooks/query/clients';
import RHFAutoComplete from 'src/components/elements/AutoComplete/RHFAutoComplete';
import { useRecruiters } from 'src/hooks/query/recruiters';
import { useJobs } from 'src/hooks/query/jobs';
import { useRoleUser } from 'src/hooks/query/usersByRole';
import moment from 'moment';

interface PopoverElementProps {
  handleClose: (element: string) => void,
  open: boolean;
  anchorEl: Element | null;
  element: string;
  children: React.ReactNode;
}

const PopoverElement = (props: PopoverElementProps) => {
  const {
    open, anchorEl, handleClose, element, children,
  } = props;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => handleClose(element)}
      className="pointer-events-none"
      classes={{
        paper: 'pointer-events-auto',
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        onMouseLeave: () => handleClose(element),
      }}
    >
      <div className="p-2 min-w-[160px] bg-white max-w-lg">
        {children}
      </div>
    </Popover>
  );
};
const validationSchema = yup.object().shape({
  searchQuery: yup.string(),
});

export type CurrentInterviewsFiltersFormValues = {
  searchQuery: string;
  dateRange: DateRange | undefined;
  clients: any;
  interviewType: any;
  jobIds: any;
  recruiter: any;
  interviewer: any;
  status: any;
};

export type CurrentInterviewsFiltersSubmitHandler = {
  values: CurrentInterviewsFiltersFormValues;
  formInstance: UseFormReturn<CurrentInterviewsFiltersFormValues, any>;
};

interface CurrentInterviewsFiltersProps {
  onSubmit: (data: CurrentInterviewsFiltersSubmitHandler) => void;
  onReset: () => void;
  currentInterview?: boolean;
}

const CurrentInterviewsFilters = function CurrentInterviewsFilters(props: CurrentInterviewsFiltersProps) {
  const { onSubmit, onReset, currentInterview } = props;
  const {
    clientOptions,
    clientsLoading,
    fetchClients,
  } = useClients({ enabled: false });

  const {
    recruitersOptions,
    recruitersLoading,
    fetchRecruiters,
  } = useRecruiters({ enabled: false });

  const {
    jobsLoading,
    jobsOptions,
    fetchJobs,
  } = useJobs({ enabled: false });

  const payload = { roleNameList: 'ATSInterviewer' };
  const {
    userByRoleOptions,
    usersLoading,
    fetchByRole,
  } = useRoleUser(payload);

  type StatusOption = {
    label: string;
    value: string;
  };

  let statusOptions: StatusOption[] = [];

  if (currentInterview) {
    statusOptions = [
      { label: 'Registered', value: 'REGISTERED' },
      { label: 'In Progress', value: 'INPROGRESS' }
    ];
  } else {
    statusOptions = [
      { label: 'Evaluation', value: 'EVALUATION' },
      { label: 'Expired', value: 'EXPIRED' },
      { label: 'Completed', value: 'COMPLETED' },
      { label: 'Disqualified', value: 'DISQUALIFIED' },
      { label: 'Aborted', value: 'ABORTED' },
      { label: 'Rejected', value: 'REJECTED' },
      { label: 'Canceled', value: 'CANCELED' }
    ];
  }
  

  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<CurrentInterviewsFiltersFormValues>({
    resolver,
  });
  const {
    handleSubmit,
  } = methods;

  const submitHandler = (data: CurrentInterviewsFiltersFormValues) => {
    onSubmit({
      values: data,
      formInstance: methods,
    });
  };
  const [anchorEl, setAnchorEl] = useState<{ [key: string]: Element | null }>({});

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>, element: string) => {
    switch (element) {
      case 'clients':
        if (clientOptions.length === 0) fetchClients();
        break;
      case 'recruiter':
        if (recruitersOptions.length === 0) fetchRecruiters();
        break;
      case 'jobIds':
        if (jobsOptions.length === 0) fetchJobs();
        break;
      case 'interviewer':
        if (userByRoleOptions.length === 0) fetchByRole();
        break;
    }
    setAnchorEl({ [element]: event.currentTarget });
  };

  const handleClose = (element: string) => {
    setAnchorEl((prev) => ({ ...prev, [element]: null }));
  };

  const clients = methods.watch('clients');
  const selectedClients = useMemo(() => clientOptions.find((clientOption) => clients === clientOption.value), [clients]);

  const jobs = methods.watch('jobIds');
  const selectedJob = useMemo(() => jobsOptions.find((jobOption) => jobs === jobOption.value), [jobs]);

  const recruiter = methods.watch('recruiter');
  const selectedRecruiter = useMemo(() => recruitersOptions.find((recruiterOption) => recruiter === recruiterOption.value), [recruiter]);

  const statuses = methods.watch('status');
  const selectedStatuses = useMemo(() => statusOptions.filter((status) => statuses?.includes(status.value)), [statuses]);

  const interviewType = methods.watch('interviewType');

  const interviewers = methods.watch('interviewer');
  const selectedInterviewer = useMemo(() => userByRoleOptions.find((interviewer) => interviewers === interviewer.value), [interviewers]);

  return (
    <>
      <form onSubmit={handleSubmit(submitHandler)} className=" mt-4 rounded-md custom-shadow bg-white">
        <div className="flex w-full items-end gap-2 py-2 p-2">
          <div className="flex-1">
            <RHFInput name="searchQuery" control={methods.control} placeholder="Search" />
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="contained" type="submit">Search</Button>
            <Button variant="outlined" type="reset" onClick={() => { methods.reset({}); onReset(); }}>Reset</Button>
          </div>
        </div>
        <div className="grid grid-cols-8 gap-2 w-full p-2 pb-2">
          <div
            onMouseEnter={(e) => handleOpen(e, 'date')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Date</span>
            <BiChevronDown className={twMerge(Boolean('date' in anchorEl && anchorEl['date']) && "rotate-180")} />
            {methods.watch('dateRange') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'date' in anchorEl ? anchorEl['date'] : null}
              open={Boolean('date' in anchorEl && anchorEl['date'])}
              handleClose={handleClose}
              element="date"
            >
              <Controller
                control={methods.control}
                name="dateRange"
                render={({ field }) => (
                  <RangeDatePicker
                    customCssClass="select-shadow"
                    locale={{
                      ...enUS,
                      options: {
                        weekStartsOn: 1,
                      },
                    }}
                    value={field.value}
                    open
                    onRangeChange={(e) => {
                      field.onChange(e);
                      handleClose('date');
                    }}
                  />
                )}
              />
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'clients')}
            onMouseLeave={(e) => handleClose('clients')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Client</span>
            <BiChevronDown className={twMerge(Boolean('clients' in anchorEl && anchorEl['clients']) && "rotate-180")} />
            {methods.watch('clients') && methods.watch('clients').length > 0 && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'clients' in anchorEl ? anchorEl['clients'] : null}
              open={Boolean('clients' in anchorEl && anchorEl['clients'])}
              handleClose={handleClose}
              element="clients"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="clients"
                  control={methods.control}
                  options={clientOptions || []}
                  loading={clientsLoading}
                  placeholder="Search clients"
                  onSelectClose={() => handleClose('clients')}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'interviewType')}
            onMouseLeave={(e) => handleClose('interviewType')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Interview Type</span>
            <BiChevronDown className={twMerge(Boolean('interviewType' in anchorEl && anchorEl['interviewType']) && "rotate-180")} />
            {methods.watch('interviewType') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'interviewType' in anchorEl ? anchorEl['interviewType'] : null}
              open={Boolean('interviewType' in anchorEl && anchorEl['interviewType'])}
              handleClose={handleClose}
              element="interviewType"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="interviewType"
                  control={methods.control}
                  options={[{ label: 'Panna', value: 'Panna' }, { label: 'Panna Live', value: 'Panna Live' }, { label: 'Client', value: 'Client' }]}
                  placeholder="Search interview type"
                  onSelectClose={() => handleClose('interviewType')}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'jobIds')}
            onMouseLeave={(e) => handleClose('jobIds')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm">
            <span className="overflow-hidden text-ellipsis text-md">Job ID</span>
            <BiChevronDown className={twMerge(Boolean('jobIds' in anchorEl && anchorEl['jobIds']) && "rotate-180")} />
            {methods.watch('jobIds') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'jobIds' in anchorEl ? anchorEl['jobIds'] : null}
              open={Boolean('jobIds' in anchorEl && anchorEl['jobIds'])}
              handleClose={handleClose}
              element="jobIds"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="jobIds"
                  control={methods.control}
                  options={jobsOptions}
                  loading={jobsLoading}
                  placeholder="Search jobIds"
                  onSelectClose={() => handleClose('jobIds')}
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'recruiter')}
            onMouseLeave={(e) => handleClose('recruiter')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Recruiter</span>
            <BiChevronDown className={twMerge(Boolean('recruiter' in anchorEl && anchorEl['recruiter']) && "rotate-180")} />
            {methods.watch('recruiter') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'recruiter' in anchorEl ? anchorEl['recruiter'] : null}
              open={Boolean('recruiter' in anchorEl && anchorEl['recruiter'])}
              handleClose={handleClose}
              element="recruiter"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="recruiter"
                  control={methods.control}
                  options={recruitersOptions}
                  loading={recruitersLoading}
                  placeholder="Search recruiter"
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'interviewer')}
            onMouseLeave={(e) => handleClose('interviewer')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Interviewer</span>
            <BiChevronDown className={twMerge(Boolean('interviewer' in anchorEl && anchorEl['interviewer']) && "rotate-180")} />
            {methods.watch('interviewer') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'interviewer' in anchorEl ? anchorEl['interviewer'] : null}
              open={Boolean('interviewer' in anchorEl && anchorEl['interviewer'])}
              handleClose={handleClose}
              element="interviewer"
            >
              <div className="min-w-[200px]">
                <RHFAutoComplete
                  name="interviewer"
                  control={methods.control}
                  options={userByRoleOptions}
                  loading={usersLoading}
                  placeholder="Search interviewer"
                />
              </div>
            </PopoverElement>
          </div>
          <div
            onMouseEnter={(e) => handleOpen(e, 'status')}
            onMouseLeave={(e) => handleClose('status')}
            className="flex cursor-pointer justify-between px-2 rounded-md relative py-1 items-center gap-2 text-sm"
          >
            <span className="overflow-hidden text-ellipsis text-md">Status</span>
            <BiChevronDown className={twMerge(Boolean('status' in anchorEl && anchorEl['status']) && "rotate-180")} />
            {methods.watch('status') && (
              <div className="h-[2px] absolute left-2 right-2 bottom-0 bg-primary" />
            )}

            <PopoverElement
              anchorEl={'status' in anchorEl ? anchorEl['status'] : null}
              open={Boolean('status' in anchorEl && anchorEl['status'])}
              handleClose={handleClose}
              element="status"
            >
              <div className="min-w-[200px]">
                <RHFMultiAutoComplete
                  name="status"
                  control={methods.control}
                  options={statusOptions}
                  placeholder="Search status"
                />
              </div>
            </PopoverElement>
          </div>
        </div>
      </form>
      <div className="flex py-2 gap-2 flex-wrap">
        {methods.watch('dateRange') && (
          <Chip
            label={`${moment(methods.watch('dateRange')?.startDate).format('MM-DD-YYYY')} - ${moment(methods.watch('dateRange')?.endDate).format('MM-DD-YYYY')}`}
            onDelete={() => {
              methods.setValue('dateRange', undefined);
            }}
          />
        )}
        {selectedClients && (
          <Chip
            label={`${selectedClients.label}`}
            onDelete={() => {
              methods.setValue('clients', undefined);
            }}
          />
        )}
        {interviewType && (
          <Chip
            label={interviewType}
            onDelete={() => {
              methods.setValue('interviewType', undefined);
            }}
          />
        )}
        {selectedJob && (
          <Chip
            label={`${selectedJob.label}`}
            onDelete={() => {
              methods.setValue('jobIds', undefined);
            }}
          />
        )}
        {selectedRecruiter && (
          <Chip
            label={`${selectedRecruiter.label}`}
            onDelete={() => {
              methods.setValue('recruiter', undefined);
            }}
          />
        )}
        {selectedInterviewer && (
          <Chip
            label={`${selectedInterviewer.label}`}
            onDelete={() => {
              methods.setValue('interviewer', undefined);
            }}
          />
        )}
        {selectedStatuses.length > 0 && (
          <Chip
            label={`Status: ${selectedStatuses.map((status) => status.label).join(', ')}`}
            onDelete={() => {
              methods.setValue('status', undefined);
            }}
          />
        )}
      </div>
    </>
  );
};

CurrentInterviewsFilters.defaultProps = {

};

CurrentInterviewsFilters.propTypes = {

};

export default CurrentInterviewsFilters;
